import React, { useRef, useState, lazy, Suspense } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCopy,
  faExpand,
  faPlus,
  faMinus,
  faTrash,
  faDownload,
  faSitemap, // tree view icon
  faFileAlt, // raw text icon
} from '@fortawesome/free-solid-svg-icons';
import {
  copyToClipboard,
  zoomIn,
  zoomOut,
  openFullscreen,
  handleDownloadTxt,
} from '../Helpers/CommonHelpers';
const ReactJson = lazy(() => import('react-json-view'));

const Output = ({
  value,
  setValue, // for clear functionality
  fontSize = 14,
  setFontSize,
  lineCol = { line: 1, column: 1 },
  setLineCol = () => {},
  idPrefix = '',
}) => {
  const outputRef = useRef(null);
  const [isTreeView, setIsTreeView] = useState(false);

  const getLineNumbers = (text) => {
    const lines = text.split('\n').length;
    return [...Array(lines)].map((_, i) => (
      <div key={i} className="line-number">
        {i + 1}
      </div>
    ));
  };

  const updateCursorPosition = () => {
    const currentRef = outputRef.current;
    if (!currentRef) return;
    const pos = currentRef.selectionStart;
    const textUptoPos = currentRef.value.slice(0, pos);
    const lines = textUptoPos.split('\n');
    setLineCol({
      line: lines.length,
      column: lines[lines.length - 1].length + 1,
    });
  };

  const syncScroll = (e) => {
    const lineNumbersDiv = document.getElementById(`${idPrefix}-line-numbers`);
    if (lineNumbersDiv) {
      lineNumbersDiv.scrollTop = e.target.scrollTop;
    }
  };

  // Try parsing JSON safely
  const getParsedJson = () => {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  return (
    <div
      className="output-editor-wrapper"
      style={{
        fontFamily: 'monospace',
        position: 'relative',
        backgroundColor: '#34495e',
        color: '#ecf0f1',
        padding: '10px',
        borderRadius: '6px',
      }}
    >
      {/* Toolbar */}
      <nav
        className="toolbar"
        aria-label="Output toolbar"
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor: '#34495e',
          borderBottom: '1px solid #2c3e50',
          padding: '6px 8px',
          display: 'flex',
          gap: '10px',
          zIndex: 10,
        }}
      >
        {/* Copy */}
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => copyToClipboard(value)}
          title="Copy to Clipboard"
          aria-label="Copy to Clipboard"
        >
          <FontAwesomeIcon icon={faCopy} />
        </button>

        {/* Zoom In */}
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => zoomIn(setFontSize)}
          title="Zoom In"
          aria-label="Zoom In"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>

        {/* Zoom Out */}
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => zoomOut(setFontSize)}
          title="Zoom Out"
          aria-label="Zoom Out"
        >
          <FontAwesomeIcon icon={faMinus} />
        </button>

        {/* Full Screen */}
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => openFullscreen(outputRef.current, 'output')}
          title="Full Screen"
          aria-label="Full Screen"
        >
          <FontAwesomeIcon icon={faExpand} />
        </button>

        {/* Download */}
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => handleDownloadTxt(value)}
          title="Download"
          aria-label="Download"
        >
          <FontAwesomeIcon icon={faDownload} />
        </button>

        {/* Clear Output */}
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => setValue('')}
          title="Clear Output"
          aria-label="Clear Output"
          disabled={!value}
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>

        {/* Toggle Tree / Raw */}
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => setIsTreeView(!isTreeView)}
          title={isTreeView ? 'Show Raw Text' : 'Show JSON Tree'}
          aria-label="Toggle View"
          disabled={!getParsedJson()}
        >
          <FontAwesomeIcon icon={isTreeView ? faFileAlt : faSitemap} />
        </button>
      </nav>

      {/* Output body */}
      <div
        className="editor-body"
        style={{
          display: 'flex',
          border: '1px solid #2c3e50',
          borderTop: 'none',
          height: '450px',
          overflow: 'hidden',
          borderRadius: '0 0 6px 6px',
          backgroundColor: '#2c3e50',
        }}
      >
        {isTreeView && getParsedJson() ? (
          <div
            style={{
              flexGrow: 1,
              padding: '10px',
              backgroundColor: '#2c3e50',
              overflow: 'auto',
              height: '100%',
            }}
          >
            <Suspense fallback={<div style={{ color: '#ecf0f1', padding: '10px' }}>Loading...</div>}>
              <ReactJson
                src={getParsedJson()}
                theme="monokai"
                collapsed={2}
                displayDataTypes={false}
                enableClipboard={true}
              />
            </Suspense>
          </div>
        ) : (
          <>
            {/* Line numbers */}
            <div
              id={`${idPrefix}-line-numbers`}
              className="line-numbers"
              aria-hidden="true"
              style={{
                backgroundColor: '#34495e',
                color: '#95a5a6',
                padding: '8px 6px',
                userSelect: 'none',
                textAlign: 'right',
                overflowY: 'auto',
                fontSize: `${fontSize}px`,
                lineHeight: '1.4',
                width: '40px',
                borderRight: '1px solid #2c3e50',
              }}
            >
              {getLineNumbers(value)}
            </div>

            {/* Readonly textarea */}
            <textarea
              ref={outputRef}
              className="output-textarea"
              value={value}
              placeholder="Output"
              readOnly
              spellCheck={false}
              aria-label="Output JSON"
              onScroll={syncScroll}
              onClick={updateCursorPosition}
              onKeyUp={updateCursorPosition}
              style={{
                flexGrow: 1,
                padding: '10px',
                fontSize: `${fontSize}px`,
                fontFamily: 'monospace',
                border: 'none',
                outline: 'none',
                resize: 'none',
                color: '#ecf0f1',
                backgroundColor: '#2c3e50',
                lineHeight: '1.4',
                height: '100%',
                overflowY: 'auto',
                cursor: 'text',
              }}
            />
          </>
        )}
      </div>

      {/* Status Bar */}
      <div
        className="status-bar"
        aria-live="polite"
        style={{
          marginTop: '8px',
          padding: '8px 12px',
          fontSize: '1rem',
          color: '#ecf0f1',
          backgroundColor: '#1a252f',
          textAlign: 'right',
          userSelect: 'none',
          borderRadius: '6px',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontWeight: '600',
          letterSpacing: '0.03em',
        }}
      >
        <span>
          Ln: {lineCol.line}, Col: {lineCol.column}
        </span>
      </div>

      <style>{`
        .toolbar-btn {
          background-color: #2c3e50;
          border: none;
          color: #ecf0f1;
          padding: 6px 10px;
          border-radius: 4px;
          cursor: pointer;
          transition: background-color 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .toolbar-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .toolbar-btn:hover:enabled,
        .toolbar-btn:focus:enabled {
          background-color: #3d566e;
          outline: none;
        }
        .line-number {
          height: 1.4em;
          line-height: 1.4;
        }
      `}</style>
    </div>
  );
};

export default Output;
